/**
 * Task Manager Context
 *
 * Centralized task management for all background operations.
 * Provides task tracking, recovery, and notification integration.
 *
 * Supported task types:
 * - kg_import: Knowledge Graph import from pickle files
 * - kg_build: Knowledge Graph building from documents
 * - kg_delete: Knowledge Graph deletion
 * - document_ingest: Document upload and processing
 * - sync: OneDrive/external data synchronization
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useTaskRecovery } from '../hooks/useTaskRecovery';
import { useNotification } from '../components/Notification/NotificationContext';
import { StoredTask, getTaskTypeLabel, formatRelativeTime } from '../services/taskStorage';
import { TaskState, TaskType, TaskStatus, isTaskTerminal, TASK_STATUS_CONFIG } from '../types/task';

// ============================================================================
// Types
// ============================================================================

export interface TaskManagerContextValue {
  /** All tracked tasks (active + recent completed) */
  trackedTasks: StoredTask[];

  /** Active tasks currently being processed */
  activeTasks: StoredTask[];

  /** Number of active tasks */
  activeTaskCount: number;

  /** Whether task recovery is in progress */
  isRecovering: boolean;

  /** Last error message */
  error: string | null;

  /**
   * Start tracking a new task
   * @param taskId - Unique task identifier from backend
   * @param taskType - Type of task (kg_import, kg_build, etc.)
   * @param namespace - Optional namespace for task isolation
   * @param metadata - Optional display metadata
   */
  trackTask: (taskId: string, taskType: TaskType, namespace?: string, metadata?: { name?: string; description?: string }) => void;

  /**
   * Stop tracking a task (removes from localStorage)
   * @param taskId - Task to stop tracking
   */
  stopTracking: (taskId: string) => void;

  /**
   * Manually refresh task status from backend
   * @param taskId - Task to refresh
   * @returns Updated task state or null if not found
   */
  refreshTaskStatus: (taskId: string) => Promise<TaskState | null>;

  /**
   * Manually trigger task recovery
   * Useful after operations that may have created tasks
   */
  recoverTasks: () => Promise<void>;

  /**
   * Clear all tracked tasks from storage
   */
  clearAllTasks: () => void;

  /**
   * Get a specific task by ID
   * @param taskId - Task ID to find
   * @returns Stored task or undefined
   */
  getTask: (taskId: string) => StoredTask | undefined;

  /**
   * Get tasks by type
   * @param taskType - Type of tasks to retrieve
   * @returns Filtered tasks
   */
  getTasksByType: (taskType: TaskType) => StoredTask[];

  /**
   * Get tasks by namespace
   * @param namespace - Namespace to filter by
   * @returns Filtered tasks
   */
  getTasksByNamespace: (namespace: string) => StoredTask[];

  /**
   * Check if any task of a specific type is running
   * @param taskType - Type to check
   * @returns True if a task of this type is running
   */
  isTaskTypeRunning: (taskType: TaskType) => boolean;
}

// ============================================================================
// Context
// ============================================================================

const TaskManagerContext = createContext<TaskManagerContextValue | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface TaskManagerProviderProps {
  children: React.ReactNode;
  /** Polling interval for active tasks in milliseconds (default: 5000) */
  pollingInterval?: number;
  /** Whether to show notifications for task completion/failure (default: true) */
  showNotifications?: boolean;
}

export const TaskManagerProvider: React.FC<TaskManagerProviderProps> = ({ children, pollingInterval = 5000, showNotifications = true }) => {
  const { addNotification } = useNotification();

  // Task completion handler
  const handleTaskComplete = useCallback(
    (task: StoredTask, _result?: TaskState) => {
      if (showNotifications) {
        const taskLabel = task.metadata?.name || getTaskTypeLabel(task.taskType);
        addNotification({
          type: 'success',
          title: 'Tác vụ hoàn thành',
          message: `${taskLabel} hoàn thành thành công`
        });
      }
    },
    [addNotification, showNotifications]
  );

  // Task failure handler
  const handleTaskFailed = useCallback(
    (task: StoredTask, error?: unknown) => {
      if (showNotifications) {
        const taskLabel = task.metadata?.name || getTaskTypeLabel(task.taskType);
        const errorMessage = error && typeof error === 'object' && 'message' in error ? (error as { message: string }).message : 'Lỗi không xác định';
        addNotification({
          type: 'error',
          title: 'Tác vụ thất bại',
          message: `${taskLabel}: ${errorMessage}`
        });
      }
    },
    [addNotification, showNotifications]
  );

  // Use the recovery hook with callbacks
  const { trackedTasks, activeTasks, isRecovering, error, trackTask, stopTracking, refreshTaskStatus, recoverTasks, clearAllTasks } = useTaskRecovery(
    {
      pollingInterval,
      onTaskComplete: handleTaskComplete,
      onTaskFailed: handleTaskFailed,
      autoRecover: true
    }
  );

  // Helper: Get task by ID
  const getTask = useCallback(
    (taskId: string): StoredTask | undefined => {
      return trackedTasks.find((t) => t.taskId === taskId);
    },
    [trackedTasks]
  );

  // Helper: Get tasks by type
  const getTasksByType = useCallback(
    (taskType: TaskType): StoredTask[] => {
      return trackedTasks.filter((t) => t.taskType === taskType);
    },
    [trackedTasks]
  );

  // Helper: Get tasks by namespace
  const getTasksByNamespace = useCallback(
    (namespace: string): StoredTask[] => {
      return trackedTasks.filter((t) => t.namespace === namespace);
    },
    [trackedTasks]
  );

  // Helper: Check if task type is running
  const isTaskTypeRunning = useCallback(
    (taskType: TaskType): boolean => {
      return activeTasks.some((t) => t.taskType === taskType);
    },
    [activeTasks]
  );

  // Memoized context value
  const value = useMemo<TaskManagerContextValue>(
    () => ({
      trackedTasks,
      activeTasks,
      activeTaskCount: activeTasks.length,
      isRecovering,
      error,
      trackTask,
      stopTracking,
      refreshTaskStatus,
      recoverTasks,
      clearAllTasks,
      getTask,
      getTasksByType,
      getTasksByNamespace,
      isTaskTypeRunning
    }),
    [
      trackedTasks,
      activeTasks,
      isRecovering,
      error,
      trackTask,
      stopTracking,
      refreshTaskStatus,
      recoverTasks,
      clearAllTasks,
      getTask,
      getTasksByType,
      getTasksByNamespace,
      isTaskTypeRunning
    ]
  );

  return <TaskManagerContext.Provider value={value}>{children}</TaskManagerContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Use the TaskManager context
 * @returns TaskManagerContextValue
 * @throws Error if used outside of TaskManagerProvider
 */
export const useTaskManager = (): TaskManagerContextValue => {
  const context = useContext(TaskManagerContext);
  if (!context) {
    throw new Error('useTaskManager must be used within a TaskManagerProvider');
  }
  return context;
};

// ============================================================================
// Utility Exports
// ============================================================================

export { getTaskTypeLabel, formatRelativeTime, isTaskTerminal, TASK_STATUS_CONFIG };
export type { StoredTask, TaskState, TaskType, TaskStatus };
